import { Briefcase, Eye, GraduationCap, MapPin, MessageSquarePlus } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CandidateBrowseRow } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import { Button } from '../../../../components/Button';
import { Checkbox, isShiftClick } from '../../../../components/Checkbox';
import { Skeleton, SkeletonLines } from '../../../../components/Skeleton';
import { EMPTY, formatDate } from '../../../../lib/format';
import { isSelected, type SelectionState } from '../../../../lib/selection';
import { educationLabel, experienceLabel } from '../../candidate-format';
import { tickRow } from './list-selection';
import { LogButtons, MoreMenu, TagButton } from './QuickActions';
import { NameLink, OverBudgetChip, PersonFlags, Score, StageChip, StarButton, TagList, type RowHandlers } from './RowBits';
import styles from './CandidateCards.module.css';

/** A card shows this many skills, then "+N more". */
const CARD_SKILLS = 4;
const SKELETON_CARDS = 6;

interface CandidateCardsProps {
  readonly rows: readonly CandidateBrowseRow[];
  readonly isLoading: boolean;
  readonly selection: SelectionState;
  readonly onSelectionChange: (selection: SelectionState) => void;
  readonly handlers: RowHandlers;
  readonly emptyState: ReactNode;
}

/** CAND-01 as cards (mockup Main.dc.html, view "cards"): the same people, the same ticks, more of each person. */
export function CandidateCards({ rows, isLoading, selection, onSelectionChange, handlers, emptyState }: CandidateCardsProps): ReactNode {
  const pageIds = rows.map((row) => row.candidate.id);
  if (isLoading && rows.length === 0) {
    return (
      <div className={styles.grid} aria-hidden>
        {Array.from({ length: SKELETON_CARDS }, (_, index) => (
          <div key={index} className={styles.card}>
            <Skeleton shape="title" width="50%" />
            <SkeletonLines count={3} />
          </div>
        ))}
      </div>
    );
  }
  if (rows.length === 0) return <div className={styles.emptyCard}>{emptyState}</div>;
  return (
    <div className={styles.grid}>
      {rows.map((row) => {
        const id = row.candidate.id;
        const isTicked = isSelected(selection, id);
        return (
          <PersonCard
            key={id}
            row={row}
            isTicked={isTicked}
            onTick={(isShift) => onSelectionChange(tickRow(selection, id, pageIds, isShift))}
            handlers={handlers}
          />
        );
      })}
    </div>
  );
}

interface PersonCardProps {
  readonly row: CandidateBrowseRow;
  readonly isTicked: boolean;
  readonly onTick: (isShift: boolean) => void;
  readonly handlers: RowHandlers;
}

function PersonCard({ row, isTicked, onTick, handlers }: PersonCardProps): ReactNode {
  const { candidate, focus } = row;
  const shownSkills = row.skills.slice(0, CARD_SKILLS);
  const moreSkills = row.skills.length - shownSkills.length;
  return (
    <article className={styles.card} data-selected={isTicked || undefined} aria-label={candidate.fullName}>
      <div className={styles.top}>
        <span className={styles.tick}>
          <Checkbox checked={isTicked} onChange={(_checked, event) => onTick(isShiftClick(event))} ariaLabel={`Select ${candidate.fullName}`} />
        </span>
        <Avatar name={candidate.fullName} size="lg" />
        <div className={styles.head}>
          <div className={styles.nameLine}>
            <NameLink row={row} handlers={handlers} className={styles.name} />
            <StarButton row={row} handlers={handlers} />
            <StageChip row={row} handlers={handlers} />
          </div>
          <span className={styles.role}>{candidate.currentTitle ?? EMPTY}</span>
          <div className={styles.meta}>
            <span>
              <MapPin size={14} aria-hidden />
              {candidate.city ?? EMPTY}
            </span>
            <span>
              <Briefcase size={14} aria-hidden />
              {experienceLabel(candidate.totalExperienceMonths) ?? EMPTY}
            </span>
            <span>
              <GraduationCap size={14} aria-hidden />
              {educationLabel(candidate.highestEducation) ?? EMPTY}
            </span>
          </div>
          <PersonFlags row={row} />
        </div>
        <div className={styles.corner}>
          <LogButtons row={row} handlers={handlers} />
          <MoreMenu row={row} />
        </div>
      </div>

      {shownSkills.length > 0 && (
        <div className={styles.indent}>
          <div className={styles.skills}>
            {shownSkills.map((skill) => (
              <span key={skill} className={styles.skill}>
                {skill}
              </span>
            ))}
            {moreSkills > 0 && <span className={styles.skill}>+{moreSkills} more</span>}
          </div>
        </div>
      )}

      <div className={`${styles.indent} ${styles.facts}`}>
        {focus !== null && (
          <span>
            Applied <b className={styles.number}>{formatDate(focus.appliedAt)}</b> for <b>{focus.jobTitle}</b>
          </span>
        )}
        {(focus?.matchScore ?? focus?.stars ?? null) !== null && (
          <span className={styles.scoreLine}>
            Match <Score row={row} />
          </span>
        )}
        <OverBudgetChip row={row} />
        <TagList tags={row.tags} />
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.open} onClick={() => handlers.onOpen(row)}>
          <Eye size={14} aria-hidden />
          Open
        </button>
        <span className={styles.grow} />
        {handlers.canEdit && (
          <>
            <Button variant="outline" size="sm" icon={MessageSquarePlus} onClick={() => handlers.onOpen(row, { tab: 'activity', log: 'note' })}>
              Note
            </Button>
            <TagButton row={row} />
          </>
        )}
      </div>
    </article>
  );
}
